import type { ReactElement, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface MatchStripProps {
  /** The match ID (A1, QUAL-B2), already formatted by `displayMatchCode`; first in the strip. */
  code?: string;
  /** Today's `header.time`, or a LIVE label. */
  leading?: ReactNode;
  court?: string;
  division?: string;
  status?: { kind: 'live' | 'upcoming' | 'outcome' | 'retired'; label: string };
  /** Screen-reader status line. */
  statusWord?: string;
  /** `data-match-status`; rendered only when passed. */
  state?: string;
  /** Right-aligned mono text. */
  meta?: ReactNode;
  /** A chevron link or play glyph. */
  trailing?: ReactNode;
}

/**
 * The one header strip for match cards, the scorecard page and the Watch panel: match ID, court, division,
 * status and a trailing glyph. It creates no positioning context (no `relative`, no transform), so
 * a stretched `after:absolute after:inset-0` link passed as `trailing` covers its card.
 */
export function MatchStrip({
  code,
  leading,
  court,
  division,
  status,
  statusWord,
  state,
  meta,
  trailing,
}: MatchStripProps): ReactElement {
  const hasStatus = statusWord !== undefined || status !== undefined || state !== undefined;

  return (
    // flex-wrap: a long meta line (the scorecard's date and length) drops under the division on a
    // phone; the division keeps at least its longest word, so it never breaks letter by letter.
    <div className="bg-surface border-border flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-0.5 border-b px-3 py-1">
      {code && (
        <span
          data-match-code
          className="text-foreground border-border-strong bg-card shrink-0 rounded border px-1.5 font-mono text-[0.6875rem] leading-4 font-semibold tracking-wider"
        >
          {code}
        </span>
      )}
      {leading}
      {court && (
        <span
          data-match-court
          className="text-muted border-border bg-card max-w-full rounded border px-1.5 font-mono text-[0.6875rem] leading-4 font-semibold tracking-wider break-words uppercase"
        >
          {court}
        </span>
      )}
      {division && (
        <span data-match-division className="grow basis-24 text-[12.5px] font-medium break-words">
          {division}
        </span>
      )}
      {/* The status and the trailing glyph wrap as one unit, so a chevron never sits alone on a line.
          No positioning context here either: a stretched link passed as `trailing` still covers its card. */}
      {(hasStatus || trailing) && (
        <span data-match-strip-end className="ml-auto flex shrink-0 items-center gap-2">
          {hasStatus && (
            <span data-match-status={state} className="leading-4">
              {statusWord !== undefined && <span className="sr-only">{statusWord}</span>}
              {status && (
                <span
                  aria-hidden="true"
                  data-retired-marker={status.kind === 'retired' ? true : undefined}
                  className={cn(
                    'text-[11px] font-bold tracking-wide uppercase',
                    status.kind === 'live' ? 'text-danger-emphasis' : 'text-muted',
                  )}
                >
                  {status.label}
                </span>
              )}
            </span>
          )}
          {trailing}
        </span>
      )}
      {meta && (
        <span
          className={cn(
            'text-muted font-mono text-[0.6875rem] leading-4',
            !division && !hasStatus && !trailing && 'ml-auto',
          )}
        >
          {meta}
        </span>
      )}
    </div>
  );
}
